import { ErrorInfo } from "react";
import axios from "axios";

// 2026-06-06 보안 — 과거 이 파일은 슬랙 webhook URL(REACT_APP_REPORT_SLACK_URL)을
// 번들에 담아 브라우저에서 직접 호출했다. 누구나 그 URL 로 슬랙에 임의 메시지 주입 가능.
// → webhook 을 백엔드 뒤로 옮김. 프론트는 우리 백엔드(/app/client-errors)로 구조화된
// 에러 정보만 보내고, 백엔드가 메시지를 조립해 텔레그램으로 알린다. 시크릿은 번들에서 제거.
const ERROR_REPORT_URL = `${process.env.REACT_APP_API ?? ""}/app/client-errors`;

const truncate = (s: string | null | undefined, max: number) =>
  s ? s.slice(0, max) : undefined;

const CHUNK_MESSAGE =
  /Loading (?:CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|'text\/html'.*is not a valid JavaScript MIME type/i;

const isChunkError = (error: Error) =>
  error?.name === "ChunkLoadError" || CHUNK_MESSAGE.test(error?.message ?? "");

/**
 * 못 받아온 청크 주소.
 * webpack 은 ChunkLoadError 에 request 를 붙이고, 메시지에도 "(error: URL)" 로 남긴다.
 * 이 값이 없으면 사후에 어떤 파일이 실패했는지 되짚을 방법이 없다.
 */
const chunkUrlOf = (error: Error): string | undefined => {
  const request = (error as Error & { request?: string }).request;
  if (request) return request;
  const found = /\((?:error|missing): (https?:\/\/[^)\s]+)\)/i.exec(
    error?.message ?? ""
  );
  return found?.[1];
};

/** index.html 이 심어둔 main 번들 script 태그 (지금 실행 중인 빌드). */
const runningBundleTag = () =>
  document.querySelector<HTMLScriptElement>(
    'script[src*="/static/js/main."]'
  ) ?? undefined;

/**
 * 지금 실행 중인 번들이 아직 배포된 최신본인지 확인한다.
 * 청크 에러의 원인이 "배포 직후 옛 번들"인지 아닌지를 가르는 유일한 근거다.
 *
 * - false : 옛 번들을 들고 있었다 → 배포 직후 현상(자동 새로고침으로 복구)
 * - true  : 옛 번들은 아니었다. 여기까지만 말해준다 — 배포 직후 전파 지연도 true 로
 *           들어오므로 "배포와 무관" 으로 읽으면 안 된다
 * - undefined : 확인 불가(manifest 접근 실패 등)
 */
const isBuildCurrent = async (): Promise<boolean | undefined> => {
  const tag = runningBundleTag();
  if (!tag?.src) return undefined;
  const bundle = tag.src.split("/").pop();
  if (!bundle) return undefined;
  try {
    const res = await fetch(
      tag.src.replace(/\/static\/js\/main\.[^/]+$/, "/asset-manifest.json"),
      { cache: "no-store" }
    );
    if (!res.ok) return undefined;
    return (await res.text()).includes(bundle);
  } catch {
    return undefined;
  }
};

/**
 * ErrorBoundary 에서 잡힌 렌더 에러를 백엔드로 리포트한다 (production 한정).
 * 비로그인 상태에서도 발생할 수 있으므로 JWT 인터셉터를 타지 않는 plain axios 사용.
 * fire-and-forget — 실패해도 앱 흐름에 영향 없음.
 */
export const reportClientError = (error: Error, info: ErrorInfo) => {
  if (process.env.REACT_APP_ENV !== "production") return;
  if (!process.env.REACT_APP_API) return;

  const chunk = isChunkError(error);
  const buildCheck = chunk
    ? isBuildCurrent()
    : Promise.resolve<boolean | undefined>(undefined);

  buildCheck.then((buildCurrent) => {
    axios
      .post(
        ERROR_REPORT_URL,
        {
          name: truncate(error.name, 200),
          stack: truncate(error.stack, 4000),
          componentStack: truncate(info?.componentStack, 4000),
          digest: truncate((info as { digest?: string })?.digest, 200),
          env: process.env.REACT_APP_ENV,
          url: typeof window !== "undefined" ? window.location.href : undefined,
          chunkUrl: chunk ? truncate(chunkUrlOf(error), 300) : undefined,
          buildCurrent,
        },
        { timeout: 5000 }
      )
      .catch(() => {
        console.log("Failed to Send Error Alert");
      });
  });
};
